/**
 * RiskTemplatesPage — CRUD surface for Risk Worksheet templates.
 *
 * Available only to the RMW Admin set (Admin / HR / Process Compliance
 * Manager). Lets you:
 *   - See every template, the count of areas/contributors, default flag.
 *   - Create a new template from scratch or duplicate the default one.
 *   - Edit a template's name, default flag, and its area + contributor tree.
 *   - Mark `is_default` (server enforces single-default).
 *   - Delete templates that are not in use by any worksheet.
 *
 * The taxonomy editor is inline: each Area is a row with a contributor
 * sub-list and "add contributor" button. Built-in (non-custom) contributors
 * are read-only chips; custom contributors added through this page are
 * deletable. Order is preserved using the `order` numeric field on both
 * Area and Contributor.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import {
  deleteData,
  fetchData,
  postData,
  putData,
} from "../../../store/actions";
import { RootState } from "../../../store/Store";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import CustomNoRowsOverlay from "../../../components/Data/NoRowsOverlayComponent";
import ConfirmDialog from "../../../components/Dialogs/ConfirmDialog";
import {
  riskTemplateDetailUrl,
  riskTemplatesUrl,
} from "../../../Service/Apis";
import { RiskArea, RiskTemplate } from "./Types/RiskManagementTypes";
import { isRmwAdmin } from "./components/rmwRoles";
import { getDefaultFactors } from "./components/defaultFactors";

/**
 * The 9 default risk areas seeded server-side by
 * `backend_django/risk_management/management/commands/seed_risk_template.py`,
 * exposed here as quick-add chips on the editor so admins can populate the
 * taxonomy with one click instead of typing each name.
 *
 * Each entry also carries the most-common contributor defaults from the
 * Excel spec — when an admin clicks the chip we pre-fill these as `custom`
 * contributors so they have a starting point they can prune or extend.
 */
interface PresetContributor {
  name: string;
  factors: string[];
}

const DEFAULT_AREA_PRESETS: ReadonlyArray<{
  name: string;
  description: string;
  contributors: PresetContributor[];
}> = [
  {
    name: "Customer",
    description: "Risks originating from the customer organisation.",
    contributors: [
      { name: "Commitment", factors: [
        "Customer's commitment to project's success",
        "Designated Project Manager from Customer",
        "Customer's Org. geared to imbibe the new system",
        "Clarity of direction",
        "Funding / Budget",
      ]},
      { name: "Knowledge", factors: [
        "Customer's knowledge in the area of: Business / Application",
        "Customer's knowledge in Technology",
        "Customer's knowledge in Development Methodology",
      ]},
      { name: "Dependency", factors: [
        "Timeliness and/or Quality of Sign-off & Reviews",
        "Timeliness and/or Quality of Decisions on technical aspects",
        "Timeliness and/or Quality of Documents",
        "Timeliness and/or Quality of Reusable components",
      ]},
      { name: "Attitude", factors: [
        "Understanding", "Participative", "Reasonable",
        "Responsiveness", "Micro management", "Expectation Level",
      ]},
      { name: "Location", factors: [
        "Communication channels between our organization and customer's organization",
        "Language of communication",
        "Currency convertibility",
        "Getting work permit/visas",
        "Infrastructure facilities in customer's country/city",
      ]},
      { name: "Hardware", factors: [
        "Target hardware platform is in use for <xyz> years",
        "Hardware to be upgraded and is / is not upgradeable",
        "Hardware planned to be procured and will be installed by <dd-mon-yyyy>",
      ]},
      { name: "System Software", factors: [
        "Target system software is in use for <xyz> years",
        "System software to be upgraded and is / is not upgradeable",
        "System software planned to be procured and will be installed by <dd-mon-yyyy>",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Customer characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Organization",
    description: "Risks arising from the delivering organisation.",
    contributors: [
      { name: "Premises", factors: [
        "Disruptions", "Safety related hazards", "Night time support",
      ]},
      { name: "Facilities", factors: [
        "Adequacy and timeliness of Sitting arrangement",
        "Adequacy and timeliness of Phone",
        "Adequacy and timeliness of Telecon",
        "Adequacy and timeliness of Videocon",
        "Adequacy and timeliness of Laptops for support",
        "Adequacy and timeliness of Mobiles/pagers",
        "Adequacy and timeliness of Electricity Supply",
      ]},
      { name: "Hardware", factors: [
        "Available within the organization",
        "Procurement Status and likelihood of delay",
        "Available configuration v/s desirable",
      ]},
      { name: "System Software", factors: [
        "Available within the organization",
        "Procurement Status and likelihood of delay",
        "Available configuration v/s desirable",
      ]},
      { name: "Productivity Tools", factors: [
        "Available within the organization",
        "Procurement Status and likelihood of delay",
        "Skills, training and experience",
      ]},
      { name: "Human Resources", factors: [
        "Timeliness of getting required skilled resources",
        "Stability / Attrition",
        "Preferred/un-preferred timings/location/role",
        "Rotation/release due",
      ]},
      { name: "SQA", factors: [
        "Knowledge and experience of CMM, Organization's Standards and Procedures",
        "SQA's effort availability",
        "Involvement of SQA in status reviews, technical reviews, and sample/system testing",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to the Organization characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Project",
    description: "Risks tied to project execution and governance.",
    contributors: [
      { name: "Size/Effort", factors: [
        "Effort Involved", "Number of users", "Number of installation sites",
        "Outstanding Problems/Enhancements", "Volume of Problems/Enhancements",
        "Underestimated",
      ]},
      { name: "Schedule", factors: [
        "Dictated by customer / upper management",
        "Schedule is not in balance with resources / requirements",
        "No flexibility in terms of phased delivery",
        "Optimistic - \"best case\" rather than \"expected case\"",
        "Schedule depends on individuals",
        "SLA terms very stringent",
        "Underestimated",
      ]},
      { name: "Criticality", factors: [
        "Is the implementation of our project related to the successful implementation of yet another project?",
        "Is the project in the \"mission critical\" category for the prospect or customer?",
      ]},
      { name: "Intergroup Dependency", factors: [
        "Number of parties involved",
        "Communication mechanism and frequency",
        "Coordination", "Escalation Mechanism",
        "Degree of Dependency", "Clarity of role of each party",
      ]},
      { name: "Information Security", factors: [
        "Version Control Checklist Compliance",
        "Source / Data Backup", "Access Rights",
        "Version Control System (Tool, Model, Branches)",
        "Build Process Control", "Release Management",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Project characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Requirements",
    description: "Risks around requirement clarity and stability.",
    contributors: [
      { name: "Stability and Quality", factors: [
        "Clarity", "Sign-off", "Outstanding issues / clarifications",
        "Creep and Size of Changes", "Assumptions",
        "Well defined acceptance criteria",
      ]},
      { name: "Volumes & Performance", factors: [
        "Peak loads", "Response Time", "Time window availability",
      ]},
      { name: "Interfaces", factors: [
        "Number of interfaces", "Batch / online", "Update / Read only",
        "Changes expected in interfacing systems",
        "Dependency on interfacing systems",
      ]},
      { name: "Special Requirements", factors: [
        "Statutory and legal requirements",
        "Security requirements", "Whether keep changing",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to the characteristics of Requirements not covered above",
      ]},
    ],
  },
  {
    name: "Team",
    description: "Risks around the delivery team itself.",
    contributors: [
      { name: "Training", factors: [
        "Very high training requirements",
        "Availability of training within the Organization/Same City/India/Abroad",
        "Timeliness and quality of training, Learning curve",
        "Well defined induction/on the job training mechanism",
      ]},
      { name: "Cohesiveness", factors: [
        "Team Size", "Motivation and Morale", "Team Goals", "Geography",
      ]},
      { name: "Project Management Expertise", factors: [
        "Experience level of PM/PL/TL",
        "Usage of planning and tracking tools",
        "Balancing of team structure",
        "Business communication skills",
      ]},
      { name: "Analysis Expertise", factors: [
        "Application knowledge within the project team/organization",
        "Number of analysts", "Analysis & system testing skills",
      ]},
      { name: "Design Expertise", factors: [
        "Platform knowledge within the project team/organization",
        "Number of designers", "Design and integration skills",
      ]},
      { name: "Programming Expertise", factors: [
        "Programming knowledge within the project team/organization on the chosen language",
        "Number of programmers and independent testers",
        "Programming and unit testing skills",
      ]},
      { name: "Vendor Personnel", factors: [
        "Fraction of total team size", "Availability", "Stability",
        "Skills", "Knowledge retention consideration",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Team characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Subcontractors",
    description: "Risks introduced by third-party vendors / subcontractors.",
    contributors: [
      { name: "Organization", factors: [
        "Size, infrastructure, financial position",
        "Past experience with our organization",
        "Past experience on similar application and technology",
        "Skill set of assigned resources",
      ]},
      { name: "Delivery", factors: [
        "Current cost overrun", "Current schedule slippage",
        "Current quality of deliverables",
      ]},
      { name: "Process Maturity", factors: [
        "Formal process maturity level/certification",
        "Software engineering methods", "Project management methods",
        "Quality assurance methods", "SCM methods",
      ]},
      { name: "Communication", factors: [
        "Interaction frequency", "Communication channels",
        "Geographic Proximity",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Subcontractor characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Process",
    description: "Risks in the process / methodology used to deliver.",
    contributors: [
      { name: "Plans and Estimates", factors: [
        "Estimation methodology used",
        "Assumptions made for estimation",
        "Availability of past productivity data",
        "Timeliness of planning / replanning",
      ]},
      { name: "Lifecycle", factors: [
        "Development / maintenance lifecycle chosen: Proven lifecycle in the Organization/Industry",
        "New trial",
      ]},
      { name: "Shortcuts", factors: [
        "Shortcuts taken on upstream activities may result in downstream rework and increased defects",
        "Deviations and non-compliance issues",
      ]},
      { name: "Standards", factors: [
        "Well defined development standards from QOL/Industry",
        "Project specific development standards",
      ]},
      { name: "Tracking", factors: [
        "Number of management reports", "Duplication of data",
        "Too much of formality", "Amount of paperwork", "Status Reviews",
      ]},
      { name: "Measurements and Analysis", factors: [
        "Right measures reflecting the project situation",
        "Timeliness of collection", "Timeliness of analysis",
        "Timeliness of actions",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Technical and Management Process characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Product",
    description: "Risks related to the product / deliverable itself.",
    contributors: [
      { name: "Analysis", factors: [
        "Proven technique", "Prior knowledge, experience and training",
        "State of functional documents / test plans",
        "Quality of Peer Review",
      ]},
      { name: "Design", factors: [
        "Architecture", "Distributed processing", "Data communication",
        "Performance/volume considerations",
        "State of technical documents / test plans",
        "Quality of Peer Review",
      ]},
      { name: "Integration", factors: [
        "Code and integrate v/s integrate at once",
        "Number of components/modules to be integrated",
      ]},
      { name: "Testing", factors: [
        "Availability of near production environment for testing (Hardware/System Software; Interfaces; Business Cases)",
        "Performance/volume considerations",
        "Well defined acceptance criteria for testing",
      ]},
      { name: "Complexity", factors: [
        "Complexity of Processing: Workflow",
        "Complexity of Processing: Logical",
        "Complexity of Processing: Mathematical",
        "Complexity of Processing: Multilingual",
      ]},
      { name: "Quality", factors: [
        "Current level of quality vis-a-vis the expected (based on review comments, number of errors, number of IUT rounds)",
        "Legacy defects",
        "Size and Quality of Reusable Components",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Product characteristics not covered above",
      ]},
    ],
  },
  {
    name: "Environment",
    description: "Risks from the operating / regulatory environment.",
    contributors: [
      { name: "Hardware Stability", factors: [
        "Maturity of Hardware",
        "Hardware platform usage within the organization",
        "Availability of hardware support in India/Customer's Country",
        "Hardware documentation",
      ]},
      { name: "Hardware Compatibility and Connectivity", factors: [
        "Number of new hardware technologies",
        "Compatibility of development and production environment",
        "Availability of application development tools",
        "Features/Limitation for required application development",
        "Connectivity to other computers",
        "Compliance with standards",
      ]},
      { name: "System Software Stability", factors: [
        "Maturity of System Software",
        "System software experience within the organization",
        "Availability of system software support in India/Customer's Country",
        "System software documentation",
      ]},
      { name: "System Software Compatibility and Connectivity", factors: [
        "Number of new system software technologies",
        "Compatibility of development and production environment",
        "Availability of tools",
        "Features/Limitation for required application development",
        "Open architecture",
        "Userfriendliness and ease of use",
        "Compliance with standards",
      ]},
      { name: "Miscellaneous", factors: [
        "Any items pertaining to Environment not covered above",
      ]},
    ],
  },
];

/**
 * Local-only drafts for the editor. They mirror `RiskArea` / `RiskContributor`
 * but allow client-side ids (`local-...`) for rows that haven't been saved
 * yet, and a `_deleted` flag so the form can defer removal until save.
 */
interface DraftContributor {
  id: string;
  name: string;
  order: number;
  is_custom: boolean;
  factors: string;
  _deleted?: boolean;
  /** True when this row was added in the current edit session. */
  _isNew?: boolean;
}

interface DraftArea {
  id: string;
  name: string;
  order: number;
  description: string;
  contributors: DraftContributor[];
  _deleted?: boolean;
  _isNew?: boolean;
}

const tempId = () => `local-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Walk a DRF error response (which can be a string, an array, or a nested
 * tree like `{areas: [{contributors: [{name: ["...required."]}]}]}`) and
 * return the first leaf string we find. Falls back to `defaultMsg`.
 *
 * Used by the editor so we can show a single, renderable error string in the
 * MUI `<Alert>` — React throws if we try to render the raw object.
 */
const flattenDrfError = (data: unknown, defaultMsg: string): string => {
  if (data == null) return defaultMsg;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) {
    for (const item of data) {
      const found = flattenDrfError(item, "");
      if (found) return found;
    }
    return defaultMsg;
  }
  if (typeof data === "object") {
    const rec = data as Record<string, unknown>;
    // Prefer canonical DRF error keys first for nicer messages.
    for (const key of ["detail", "non_field_errors"]) {
      if (rec[key] !== undefined) {
        const found = flattenDrfError(rec[key], "");
        if (found) return found;
      }
    }
    for (const [key, value] of Object.entries(rec)) {
      const inner = flattenDrfError(value, "");
      if (inner) return key === "non_field_errors" ? inner : `${key}: ${inner}`;
    }
  }
  return defaultMsg;
};

const toDrafts = (areas: RiskArea[]): DraftArea[] =>
  [...areas]
    .sort((a, b) => a.order - b.order)
    .map((a) => ({
      id: a.id,
      name: a.name,
      order: a.order,
      description: a.description ?? "",
      contributors: [...a.contributors]
        .sort((c1, c2) => c1.order - c2.order)
        .map((c) => ({
          id: c.id,
          name: c.name,
          order: c.order,
          is_custom: c.is_custom,
          factors: c.factors || getDefaultFactors(a.name, c.name),
        })),
    }));

const stripDrafts = (areas: DraftArea[]) =>
  areas
    .filter((a) => !a._deleted)
    .map((a, areaIdx) => ({
      ...(a._isNew ? {} : { id: a.id }),
      name: a.name,
      order: areaIdx,
      description: a.description,
      contributors: a.contributors
        .filter((c) => !c._deleted)
        .map((c, cIdx) => ({
          ...(c._isNew ? {} : { id: c.id }),
          name: c.name,
          order: cIdx,
          is_custom: c.is_custom,
          factors: c.factors,
        })),
    }));

const RiskTemplatesPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );

  // Only the RMW admin set can manage templates. Everyone else goes to the
  // dashboard (Process Compliance Officer) or landing (Manager / Employee).
  useEffect(() => {
    if (!isRmwAdmin(selectedRole)) {
      if (selectedRole?.toLowerCase() === "process compliance officer") {
        navigate("/pms/risk-management/dashboard", { replace: true });
      } else {
        navigate("/landing", { replace: true });
      }
    }
  }, [selectedRole, navigate]);

  const [templates, setTemplates] = useState<RiskTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  // Editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<RiskTemplate | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftIsDefault, setDraftIsDefault] = useState(false);
  const [draftAreas, setDraftAreas] = useState<DraftArea[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<RiskTemplate | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    dispatch(fetchData(`${riskTemplatesUrl}?page_size=200`))
      .then((res: any) => {
        const list: RiskTemplate[] = res?.results ?? res ?? [];
        setTemplates(list);
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // -------------------------------------------------------------------------
  // Editor open/close
  // -------------------------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    setDraftName("");
    setDraftIsDefault(false);
    setDraftAreas([]);
    setFormError("");
    setEditorOpen(true);
  };

  const openEdit = (tpl: RiskTemplate) => {
    setEditing(tpl);
    setDraftName(tpl.name);
    setDraftIsDefault(!!tpl.is_default);
    setDraftAreas(toDrafts(tpl.areas ?? []));
    setFormError("");
    setEditorOpen(true);
  };

  const openDuplicate = (tpl: RiskTemplate) => {
    setEditing(null);
    setDraftName(`${tpl.name} (copy)`);
    setDraftIsDefault(false);
    setDraftAreas(
      toDrafts(tpl.areas ?? []).map((a) => ({
        ...a,
        id: tempId(),
        _isNew: true,
        contributors: a.contributors.map((c) => ({
          ...c,
          id: tempId(),
          _isNew: true,
          is_custom: true,
          factors: c.factors ?? "",
        })),
      }))
    );
    setFormError("");
    setEditorOpen(true);
  };

  // -------------------------------------------------------------------------
  // Area / contributor mutations on the draft
  // -------------------------------------------------------------------------

  const addArea = () => {
    setDraftAreas((prev) => [
      ...prev,
      {
        id: tempId(),
        name: "",
        order: prev.length,
        description: "",
        contributors: [],
        _isNew: true,
      },
    ]);
  };

  /**
   * One-click insert of one of the 9 default areas (with its preset
   * contributors). Skips the area if a non-deleted one with the same name
   * already exists in the draft.
   */
  const addPresetArea = (preset: (typeof DEFAULT_AREA_PRESETS)[number]) => {
    setDraftAreas((prev) => {
      const exists = prev.some(
        (a) =>
          !a._deleted && a.name.trim().toLowerCase() === preset.name.toLowerCase()
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          id: tempId(),
          name: preset.name,
          order: prev.length,
          description: preset.description,
          contributors: preset.contributors.map((c, idx) => ({
            id: tempId(),
            name: c.name,
            order: idx,
            is_custom: true,
            factors: c.factors.join("\n"),
            _isNew: true,
          })),
          _isNew: true,
        },
      ];
    });
  };

  /** Bulk insert: add every preset that isn't already in the draft. */
  const addAllPresetAreas = () => {
    DEFAULT_AREA_PRESETS.forEach((p) => addPresetArea(p));
  };

  const updateArea = (id: string, patch: Partial<DraftArea>) => {
    setDraftAreas((prev) =>
      prev.map((a) => (a.id === id ? { ...a, ...patch } : a))
    );
  };

  const removeArea = (id: string) => {
    setDraftAreas((prev) =>
      prev
        .map((a) =>
          a.id === id ? (a._isNew ? null : { ...a, _deleted: true }) : a
        )
        .filter((a): a is DraftArea => !!a)
    );
  };

  const moveArea = (id: string, dir: -1 | 1) => {
    setDraftAreas((prev) => {
      const visible = prev.filter((a) => !a._deleted);
      const idx = visible.findIndex((a) => a.id === id);
      if (idx < 0) return prev;
      const target = idx + dir;
      if (target < 0 || target >= visible.length) return prev;
      const reordered = [...visible];
      [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
      // Re-merge with any deleted (kept for diff submission) preserving order.
      const deleted = prev.filter((a) => a._deleted);
      return [
        ...reordered.map((a, i) => ({ ...a, order: i })),
        ...deleted,
      ];
    });
  };

  const addContributor = (areaId: string) => {
    setDraftAreas((prev) =>
      prev.map((a) =>
        a.id === areaId
          ? {
              ...a,
              contributors: [
                ...a.contributors,
                {
                  id: tempId(),
                  name: "",
                  order: a.contributors.length,
                  is_custom: true,
                  factors: "",
                  _isNew: true,
                },
              ],
            }
          : a
      )
    );
  };

  const updateContributor = (
    areaId: string,
    contribId: string,
    patch: Partial<DraftContributor>
  ) => {
    setDraftAreas((prev) =>
      prev.map((a) =>
        a.id === areaId
          ? {
              ...a,
              contributors: a.contributors.map((c) =>
                c.id === contribId ? { ...c, ...patch } : c
              ),
            }
          : a
      )
    );
  };

  const removeContributor = (areaId: string, contribId: string) => {
    setDraftAreas((prev) =>
      prev.map((a) =>
        a.id === areaId
          ? {
              ...a,
              contributors: a.contributors
                .map((c) =>
                  c.id === contribId
                    ? c._isNew
                      ? null
                      : { ...c, _deleted: true }
                    : c
                )
                .filter((c): c is DraftContributor => !!c),
            }
          : a
      )
    );
  };

  // -------------------------------------------------------------------------
  // Save / delete
  // -------------------------------------------------------------------------

  const handleSave = async () => {
    if (!draftName.trim()) {
      setFormError("Template name is required.");
      return;
    }
    const visibleAreas = draftAreas.filter((a) => !a._deleted);
    if (visibleAreas.length === 0) {
      setFormError("Add at least one risk area.");
      return;
    }
    for (const a of visibleAreas) {
      if (!a.name.trim()) {
        setFormError("Every risk area needs a name.");
        return;
      }
      for (const c of a.contributors.filter((c) => !c._deleted)) {
        if (!c.name.trim()) {
          setFormError(`Every contributor in "${a.name}" needs a name.`);
          return;
        }
      }
    }

    setSaving(true);
    setFormError("");
    try {
      const payload = {
        name: draftName.trim(),
        is_default: draftIsDefault,
        areas: stripDrafts(draftAreas),
      };
      if (editing) {
        await dispatch(putData(riskTemplateDetailUrl(editing.id), payload));
        showToast("Template updated.", "success", false);
      } else {
        await dispatch(postData(riskTemplatesUrl, payload));
        showToast("Template created.", "success", false);
      }
      setEditorOpen(false);
      fetchTemplates();
    } catch (err: any) {
      const data = err?.response?.data;
      setFormError(flattenDrfError(data, "Failed to save template."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    try {
      await dispatch(deleteData(riskTemplateDetailUrl(deleteTarget.id)));
      showToast("Template deleted.", "success", false);
      setDeleteTarget(null);
      fetchTemplates();
    } catch (err: any) {
      const data = err?.response?.data;
      showToast(
        data?.detail ?? "Failed to delete template. It may be in use.",
        "error",
        false
      );
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleToggleDefault = async (tpl: RiskTemplate) => {
    try {
      await dispatch(
        putData(riskTemplateDetailUrl(tpl.id), {
          name: tpl.name,
          is_default: !tpl.is_default,
          areas: stripDrafts(toDrafts(tpl.areas ?? [])),
        })
      );
      showToast(
        !tpl.is_default
          ? "Set as default template."
          : "Default flag cleared.",
        "success",
        false
      );
      fetchTemplates();
    } catch {
      showToast("Failed to toggle default flag.", "error", false);
    }
  };

  // -------------------------------------------------------------------------
  // Derived rows
  // -------------------------------------------------------------------------

  const rows = useMemo(
    () =>
      templates.map((t) => ({
        id: t.id,
        name: t.name,
        is_default: t.is_default,
        area_count: t.areas?.length ?? 0,
        contributor_count:
          t.areas?.reduce(
            (sum, a) => sum + (a.contributors?.length ?? 0),
            0
          ) ?? 0,
        created_by_name: t.created_by_name ?? "—",
        created_at: t.created_at,
        raw: t,
      })),
    [templates]
  );

  const columns: GridColDef[] = [
    { field: "name", headerName: "Template", flex: 1.4, minWidth: 220 },
    {
      field: "is_default",
      headerName: "Default",
      width: 110,
      renderCell: (p: GridRenderCellParams) => (
        <Tooltip
          title={p.value ? "Default for new worksheets" : "Set as default"}
        >
          <IconButton
            size="small"
            color={p.value ? "warning" : "default"}
            onClick={() => handleToggleDefault(p.row.raw)}
          >
            {p.value ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        </Tooltip>
      ),
    },
    { field: "area_count", headerName: "Areas", width: 100, type: "number" },
    {
      field: "contributor_count",
      headerName: "Contributors",
      width: 130,
      type: "number",
    },
    { field: "created_by_name", headerName: "Created by", flex: 1, minWidth: 160 },
    {
      field: "created_at",
      headerName: "Created",
      width: 140,
      valueFormatter: ({ value }) =>
        value ? new Date(value as string).toLocaleDateString() : "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 200,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row.raw)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate">
            <IconButton size="small" onClick={() => openDuplicate(p.row.raw)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              p.row.is_default
                ? "Default template cannot be deleted"
                : "Delete"
            }
          >
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={p.row.is_default}
                onClick={() => setDeleteTarget(p.row.raw)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // -------------------------------------------------------------------------
  // Editor body
  // -------------------------------------------------------------------------

  const renderAreaEditor = (area: DraftArea, areaIdx: number) => {
    const visibleContribs = area.contributors.filter((c) => !c._deleted);
    return (
      <Paper key={area.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip size="small" label={`#${areaIdx + 1}`} />
          <TextField
            size="small"
            label="Area name"
            value={area.name}
            onChange={(e) => updateArea(area.id, { name: e.target.value })}
            sx={{ flex: 1 }}
          />
          <Tooltip title="Move up">
            <span>
              <IconButton
                size="small"
                onClick={() => moveArea(area.id, -1)}
                disabled={areaIdx === 0}
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Move down">
            <IconButton size="small" onClick={() => moveArea(area.id, 1)}>
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove area">
            <IconButton
              size="small"
              color="error"
              onClick={() => removeArea(area.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <TextField
          size="small"
          label="Description (optional)"
          value={area.description}
          onChange={(e) =>
            updateArea(area.id, { description: e.target.value })
          }
          fullWidth
          sx={{ mb: 1.5 }}
        />
        <Typography variant="caption" color="text.secondary">
          Contributors
        </Typography>
        <Stack spacing={1} sx={{ mt: 0.5 }}>
          {visibleContribs.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              No contributors yet.
            </Typography>
          )}
          {visibleContribs.map((c) => {
            const factorLines = c.factors
              ? c.factors.split("\n").filter((f) => f.trim())
              : [];
            return (
              <Paper
                key={c.id}
                variant="outlined"
                sx={{ p: 1.5, bgcolor: "grey.50" }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    value={c.name}
                    placeholder="Contributor name"
                    onChange={(e) =>
                      updateContributor(area.id, c.id, {
                        name: e.target.value,
                      })
                    }
                    sx={{ flex: 1 }}
                  />
                  <Chip
                    size="small"
                    label={c.is_custom ? "custom" : "built-in"}
                    color={c.is_custom ? "default" : "info"}
                    variant="outlined"
                  />
                  <Tooltip title="Remove contributor">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeContributor(area.id, c.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {factorLines.length > 0 && (
                  <Box sx={{ mt: 0.75, ml: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Factors:
                    </Typography>
                    <Box
                      component="ul"
                      sx={{
                        m: 0,
                        pl: 2.5,
                        "& li": {
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          py: 0.1,
                        },
                      }}
                    >
                      {factorLines.map((f, fi) => (
                        <li key={fi}>{f}</li>
                      ))}
                    </Box>
                  </Box>
                )}
                <TextField
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={6}
                  value={c.factors}
                  placeholder="One factor per line (e.g. Clarity of direction)"
                  onChange={(e) =>
                    updateContributor(area.id, c.id, {
                      factors: e.target.value,
                    })
                  }
                  fullWidth
                  sx={{ mt: 1 }}
                  InputProps={{
                    sx: { fontSize: "0.8rem" },
                  }}
                  label="Assessment factors"
                />
              </Paper>
            );
          })}
        </Stack>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => addContributor(area.id)}
          sx={{ mt: 1 }}
        >
          Add contributor
        </Button>
      </Paper>
    );
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Risk Templates</PageTitle>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: "center", py: 1.5 }}>
              <Typography variant="h4" fontWeight="bold" color="#1976d2">
                {templates.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Templates
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: "center", py: 1.5 }}>
              <Typography variant="h4" fontWeight="bold" color="#ed6c02">
                {templates.filter((t) => t.is_default).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Default
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ textAlign: { sm: "right" } }}>
          <Stack
            direction="row"
            spacing={1}
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openCreate}
            >
              New template
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* --------------------- Editor dialog --------------------- */}
      <Dialog
        open={editorOpen}
        onClose={() => !saving && setEditorOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          {editing ? "Edit template" : "New template"}
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setEditorOpen(false)} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Template name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={draftIsDefault}
                    onChange={(e) => setDraftIsDefault(e.target.checked)}
                  />
                }
                label="Default template"
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="subtitle1">Risk areas & contributors</Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addArea}
            >
              Add custom area
            </Button>
          </Box>

          {/* Quick-add chips for the 9 default risk areas. Clicking a chip
              inserts the area (and its preset contributors) into the draft.
              Already-added areas are dimmed and disabled. */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
              flexWrap="wrap"
            >
              <Typography variant="body2" color="text.secondary">
                Quick add from default taxonomy:
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="outlined"
                onClick={addAllPresetAreas}
                disabled={DEFAULT_AREA_PRESETS.every((p) =>
                  draftAreas.some(
                    (a) =>
                      !a._deleted &&
                      a.name.trim().toLowerCase() === p.name.toLowerCase()
                  )
                )}
              >
                Load all 9
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {DEFAULT_AREA_PRESETS.map((preset) => {
                const alreadyAdded = draftAreas.some(
                  (a) =>
                    !a._deleted &&
                    a.name.trim().toLowerCase() === preset.name.toLowerCase()
                );
                return (
                  <Tooltip
                    key={preset.name}
                    title={
                      alreadyAdded
                        ? `${preset.name} already added`
                        : `Add "${preset.name}" with ${preset.contributors.length} default contributor(s)`
                    }
                  >
                    <span>
                      <Chip
                        label={preset.name}
                        size="small"
                        color={alreadyAdded ? "default" : "primary"}
                        variant={alreadyAdded ? "outlined" : "filled"}
                        icon={alreadyAdded ? undefined : <AddCircleOutlineIcon />}
                        onClick={
                          alreadyAdded ? undefined : () => addPresetArea(preset)
                        }
                        disabled={alreadyAdded}
                        sx={{
                          cursor: alreadyAdded ? "default" : "pointer",
                          opacity: alreadyAdded ? 0.55 : 1,
                        }}
                      />
                    </span>
                  </Tooltip>
                );
              })}
            </Stack>
          </Paper>

          {draftAreas.filter((a) => !a._deleted).length === 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              No areas yet. Click a chip above to insert a default risk area,
              tap <b>Load all 9</b> to insert the full taxonomy at once, or use{" "}
              <b>Add custom area</b> for one that isn't on the list.
            </Alert>
          )}

          {draftAreas
            .map((a, idx) => ({ a, idx }))
            .filter(({ a }) => !a._deleted)
            .map(({ a }, visibleIdx) => renderAreaEditor(a, visibleIdx))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {editing ? "Save changes" : "Create template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --------------------- Delete confirm --------------------- */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete template?"
        severity="error"
        confirmButtonText={deleteSaving ? "Deleting..." : "Delete"}
        message={
          deleteTarget ? (
            <>
              You are about to delete <b>{deleteTarget.name}</b>. This cannot
              be undone. Worksheets currently using this template will block
              the delete on the server side.
            </>
          ) : (
            ""
          )
        }
        onCancel={() => !deleteSaving && setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Container>
  );
};

export default RiskTemplatesPage;
